import { useCallback, useEffect, useRef, useState } from "react";

export const AUTO_DISMISS_MS = 3000;

export type TransientBannerVariant = "success" | "info" | "error";

type TransientMessageState = {
  text: string;
  variant: TransientBannerVariant;
};

function resolveAutoDismissMs(
  variant: TransientBannerVariant,
  defaultAutoDismissMs: number | null | undefined,
  override?: number | null,
): number | null {
  if (override !== undefined) return override;
  if (defaultAutoDismissMs !== undefined) {
    return variant === "error" ? null : defaultAutoDismissMs;
  }
  return variant === "error" ? null : AUTO_DISMISS_MS;
}

export function useTransientMessage(options?: { autoDismissMs?: number | null }) {
  const defaultAutoDismissMs = options?.autoDismissMs;
  const [state, setState] = useState<TransientMessageState | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setState(null);
  }, [clearTimer]);

  const show = useCallback(
    (
      text: string,
      variant: TransientBannerVariant = "success",
      autoDismissMsOverride?: number | null,
    ) => {
      clearTimer();
      setState({ text, variant });
      const autoDismissMs = resolveAutoDismissMs(variant, defaultAutoDismissMs, autoDismissMsOverride);
      if (autoDismissMs != null && autoDismissMs > 0) {
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          setState(null);
        }, autoDismissMs);
      }
    },
    [clearTimer, defaultAutoDismissMs],
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  return {
    message: state?.text ?? null,
    variant: (state?.variant ?? "success") as TransientBannerVariant,
    show,
    dismiss,
  };
}
